/**
 * contacts.service.ts — Layer 3: Contacts Domain
 */

import { getRequest, deleteRequest, postRequest, putRequest } from './http.service';
import {
  URL_CONTACTS_BY_CLIENT,
  URL_CONTACTS_LIST_BY_ORG,
  URL_CONTACTS_LIST,
  URL_CLIENTS_BY_CONTACT,
  URL_CONTACT_BY_ID,
  URL_CONTACT_DELETE_BY_ID,
  URL_CONTACT_ADD,
  URL_CONTACT_UPDATE_BY_ID,
  URL_CONTACT_MERGE,
  URL_CHECK_DUPLICATES,
  URL_CONTACTS_LOOKUP,
  URL_CONTACT_IMPORT_DRY_RUN,
  URL_CONTACT_IMPORT,
} from './url.service';

/** Get all contacts linked to a specific client */
export const fetchContactsByClient = (clientId: string) =>
  getRequest(URL_CONTACTS_BY_CLIENT(clientId));

/** Get all contacts across an org (used when no client is selected) */
export const fetchOrgContacts = (orgId: string) =>
  getRequest(URL_CONTACTS_LIST_BY_ORG(orgId));

/** Get all contacts (no org filter — used by LinkContactModal) */
export const fetchAllContacts = () =>
  getRequest(URL_CONTACTS_LIST);

/** Get all clients linked to a specific contact */
export const fetchClientsByContact = (contactId: string) =>
  getRequest(URL_CLIENTS_BY_CONTACT(contactId));

/** Get a single contact by ID */
export const fetchContactById = (contactId: string) =>
  getRequest(URL_CONTACT_BY_ID(contactId));

/** Delete a contact */
export const deleteContact = (contactId: string) =>
  deleteRequest(URL_CONTACT_DELETE_BY_ID(contactId));

/** Create a new contact (force=true bypasses the duplicate-contact check) */
export const createContact = (payload: Record<string, unknown>, force = false) =>
  postRequest(URL_CONTACT_ADD, force ? { ...payload, force: true } : payload);

/** Update an existing contact */
export const updateContact = (contactId: string, payload: Record<string, unknown>) =>
  putRequest(URL_CONTACT_UPDATE_BY_ID(contactId), payload);

/** Merge a duplicate contact into an existing one */
export const mergeContact = (contactId: string, payload: Record<string, unknown>) =>
  putRequest(URL_CONTACT_MERGE(contactId), payload);

/** Check whether a contact with this name/email already exists */
export const checkDuplicateContact = (payload: Record<string, unknown>) =>
  postRequest(URL_CHECK_DUPLICATES, payload);

/** Look up a contact by email address */
export const lookupContactByEmail = (email: string) =>
  getRequest(URL_CONTACTS_LOOKUP(email));

/** Dry-run a bulk contact import — detects duplicates without writing anything */
export const dryRunContactImport = (contacts: unknown[]) =>
  postRequest(URL_CONTACT_IMPORT_DRY_RUN, { contacts });

/** Execute a bulk contact import */
export const executeContactImport = (contacts: unknown[]) =>
  postRequest(URL_CONTACT_IMPORT, { contacts });
